import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { StyledPageTitle } from 'theme/styles';
import { useTranslations } from 'hooks/useTranslations';
import AuditLogSearchForm from 'pages/AuditLogPage/AuditLogSearchForm';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { TableTopButtons } from 'components/atoms/TableTopButtons';
import { useAuditLog } from 'pages/AuditLogPage/hooks';
import { EPerPages, ISortOptions } from 'models/table';
import { IAuditLogSearchFormValues, initialAuditLogSearchValues } from 'models/auditLog';
import { Pagination } from 'components/molecules/Pagination';
import { Table } from 'components/molecules/Table';
import { auditLogTableColumns, mapAuditLogToDataSource } from 'pages/AuditLogPage/helpers';
import { exportAuditLogCSV } from 'requests/report';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { CardList } from 'components/molecules/CardList';
import AuditLogTableCard from 'pages/AuditLogPage/AuditLogTableCard';
import { useLang } from 'models/langContext';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { Loader } from 'components/atoms/Loader';
import { Tooltip } from '@mui/material';
import { isExportNotAvailable } from 'constants/Export';
import { useLocation, useSearchParams } from 'react-router-dom';
import { objectToQueryString, prepareQueryValues } from 'helpers/searchQuery';
import { areDatesEqual, formatDateTimeToIso } from 'utils/date';
import { areObjectsEqual } from 'utils/objects';
import { replaceUrlIfOnPage } from 'utils/history';

const AuditLogPage = () => {
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { pathname }: any = useLocation();
  const { isDesktop } = useThemeBreakpoint();
  const { getAuditLogData, auditLogs, paginator } = useAuditLog();
  const { hasPermissions } = usePermission();
  const [searchParams] = useSearchParams();
  const perPageFromQuery = Number.parseInt(searchParams.get('perPage') || '25', 10);
  const currentPageFromQuery = Number.parseInt(searchParams.get('currentPage') || '1', 10);
  const [currentPage, setCurrentPage] = useState<number>(
    currentPageFromQuery > 0 ? currentPageFromQuery : 1,
  );
  const [perPage, setPerPage] = useState<EPerPages>(
    [25, 50, 100].includes(perPageFromQuery)
      ? (perPageFromQuery as EPerPages)
      : EPerPages.perPage25,
  );
  const [auditLogQuery, setAuditLogQuery] = useState<Partial<IAuditLogSearchFormValues>>({});
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const { midDateFormat } = useGlobalProperty();
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialValues, setInitialValues] = useState<IAuditLogSearchFormValues | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    const query = new URLSearchParams(searchParams);
    const queryValues = prepareQueryValues(
      initialAuditLogSearchValues,
      query,
      ['eventDateFrom', 'eventDateTo'].flat(),
    );
    setInitialValues({ ...initialAuditLogSearchValues, ...queryValues });
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      if (initialValues === null) {
        return;
      }
      if (!auditLogs.length) {
        setIsLoading(true);
      }
      setIsPaginationLoading(true);
      await getAuditLogData(currentPage, perPage, auditLogQuery, sort);
      setIsPaginationLoading(false);
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, auditLogQuery, sort, selectedLanguage]);

  const handleSetAuditLogQuery = (values: Partial<IAuditLogSearchFormValues>) => {
    const eventDateFrom = values.eventDateFrom
      ? formatDateTimeToIso(values.eventDateFrom)
      : null;
    const eventDateTo = values.eventDateTo ? formatDateTimeToIso(values.eventDateTo) : null;

    const query = objectToQueryString({
      ...values,
      eventDateFrom,
      eventDateTo,
      currentPage,
      perPage,
    });
    if (query.length) {
      replaceUrlIfOnPage(`${pathname}?${query}`);
    }
    if (!isInitialLoad) {
      if (
        !areDatesEqual(values.eventDateFrom, auditLogQuery.eventDateFrom, true) ||
        !areDatesEqual(values.eventDateTo, auditLogQuery.eventDateTo, true) ||
        !areObjectsEqual(values.eventTypesUniqueIds, auditLogQuery.eventTypesUniqueIds) ||
        !areObjectsEqual(values.modulesUniqueIds, auditLogQuery.modulesUniqueIds) ||
        !areObjectsEqual(values.accountsUniqueIds, auditLogQuery.accountsUniqueIds)
      ) {
        setCurrentPage(1);
      }
    } else {
      setIsInitialLoad(false);
    }

    setAuditLogQuery(values);
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('user.breadcrumbs.administration'), route: ERouteLinks.Administration },
          t('auditLogDetails.breadcrumbs.auditLog'),
        ]}
      />
      <StyledPageTitle variant="h4">
        {t('auditLogDetails.breadcrumbs.auditLog')}
      </StyledPageTitle>

      {initialValues && (
        <AuditLogSearchForm
          onSubmit={handleSetAuditLogQuery}
          initialSearchForm={initialValues}
          isSearchButtonDisabled={isLoading}
          currentPage={currentPage}
          perPage={perPage}
        />
      )}

      {hasPermissions(EPermission.EXPORT_AUDIT_LOG) && (
        <TableTopButtons>
          <Grid columns={10} sx={{ justifyContent: 'flex-end' }}>
            <Grid size={{ xs: 10, sm: 12, lg: 2 }} sx={{ marginRight: '10px' }}>
              {!isLoading &&
                (isExportNotAvailable(paginator) ? (
                  <Tooltip title={t('dataExportRecordsLimitAmount.label')} arrow>
                    <span>
                      <Button
                        label={t('searchForAuditLog.export.button')}
                        variant={EButtonVariants.outlined}
                        onClick={() => {
                          setIsExportLoading(true);
                          exportAuditLogCSV(auditLogQuery).finally(() => {
                            setIsExportLoading(false);
                          });
                        }}
                        fullWidth
                        size={EButtonSizes.small}
                        isLoading={isExportLoading}
                        disabled
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    label={t('searchForAuditLog.export.button')}
                    variant={EButtonVariants.outlined}
                    onClick={() => {
                      setIsExportLoading(true);
                      exportAuditLogCSV(auditLogQuery).finally(() => {
                        setIsExportLoading(false);
                      });
                    }}
                    fullWidth
                    size={EButtonSizes.small}
                    isLoading={isExportLoading}
                  />
                ))}
            </Grid>
          </Grid>
        </TableTopButtons>
      )}
      {isLoading ? (
        <Loader isLoading={isLoading} />
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {isDesktop ? (
            <Table
              columns={auditLogTableColumns({
                dateFormat: midDateFormat,
              })}
              dataSource={mapAuditLogToDataSource(auditLogs)}
              onSort={setSort}
            />
          ) : (
            <CardList
              items={mapAuditLogToDataSource(auditLogs)}
              render={(data, index) => <AuditLogTableCard data={data} key={index} />}
            />
          )}
        </>
      )}

      {paginator && (
        <Pagination
          count={paginator.totalElements}
          onChangePage={(page) => setCurrentPage(page)}
          current={currentPage}
          perPage={perPage}
          onChangePerPage={(value) => setPerPage(value)}
          isLoading={isPaginationLoading}
        />
      )}
    </>
  );
};

export default AuditLogPage;
